import { Divider, Text, Box, Heading, Table, Thead, Tbody } from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollSync, ScrollSyncPane } from 'react-scroll-sync';

import ExportMenu from 'containers/Reports/shared/ExportMenu';
import { ReportsTableHeadCell, ReportsTableDataCell } from 'containers/Reports/shared/ReportsTableCell';
import ReportsTableRow from 'containers/Reports/shared/ReportsTableRow';
import useStickyHeader from 'containers/Reports/shared/useStickyHeader';
import { useUserObject } from 'store/useUserStore';
import { formatCurrency, formatDistanceUnit } from 'utils/format';
import { isDistanceInMiles } from 'utils/helpers';

const ReportsByTotalTable: React.FC<{ data: any; dateRange: Array<any> }> = ({ data, dateRange }) => {
  const { t } = useTranslation(['common', 'reports']);
  useStickyHeader();

  const userStore = useUserObject();

  return (
    <>
      <Divider my="4" />
      <Box p="4">
        <Heading variant="h2" size="md" fontWeight="semibold" mb="4">
          {t('reports:searchResults')}
        </Heading>
        {data?.payload?.attributes ? (
          <ExportMenu
            dateRange={dateRange}
            attributes={data?.payload?.attributes}
            reportType="byTotalOnly"
            displaySettings={data?.payload?.integrations}
          />
        ) : null}
        <ScrollSync>
          <div style={{ position: 'relative' }}>
            <Table display="block" id="js-report-table">
              <ScrollSyncPane group="horizontal">
                <Thead
                  display="block"
                  position="sticky"
                  top="0"
                  background="primary.50"
                  max-width="100%"
                  overflow="auto"
                  sx={{
                    '&::-webkit-scrollbar': { display: 'none' },
                    '-ms-overflow-style': 'none',
                    scrollbarWidth: 'none',
                  }}
                >
                  <ReportsTableRow color="gray.600" mb="0" border="0">
                    <ReportsTableHeadCell width="200px">{t('common:name')}</ReportsTableHeadCell>
                    <ReportsTableHeadCell>
                      {isDistanceInMiles() ? t('reports:totalMileage') : t('reports:totalKilometers')}
                    </ReportsTableHeadCell>
                    <ReportsTableHeadCell>{t('reports:regularHours')}</ReportsTableHeadCell>
                    <ReportsTableHeadCell>{t('reports:regularPay')}</ReportsTableHeadCell>
                    {userStore?.companyInfo?.include_premium_pay ? (
                      <ReportsTableHeadCell>{t('reports:premiumPayHours')}</ReportsTableHeadCell>
                    ) : null}
                    {userStore?.companyInfo?.include_premium_pay ? (
                      <ReportsTableHeadCell>{t('reports:premiumPayCost')}</ReportsTableHeadCell>
                    ) : null}
                    <ReportsTableHeadCell>{t('reports:OTHrs')}</ReportsTableHeadCell>
                    <ReportsTableHeadCell>{t('reports:DOThrs')}</ReportsTableHeadCell>
                    <ReportsTableHeadCell>{t('reports:OTCost')}</ReportsTableHeadCell>
                    <ReportsTableHeadCell>{t('reports:totalTime')}</ReportsTableHeadCell>
                    <ReportsTableHeadCell>{t('reports:totalCost')}</ReportsTableHeadCell>
                    <ReportsTableHeadCell width="180px">
                      {isDistanceInMiles() ? t('reports:totalMileageCost') : t('reports:totalKilometersCost')}
                    </ReportsTableHeadCell>
                  </ReportsTableRow>
                </Thead>
              </ScrollSyncPane>

              <ScrollSyncPane group="horizontal">
                <Tbody display="block" max-width="100%" overflow="auto" mt={2}>
                  {Array.isArray(data?.payload?.users) &&
                    data?.payload?.users &&
                    data.payload.users.map((user: any) => (
                      <ReportsTableRow key={user?.user_id} width="fit-content" minWidth="100%">
                        <ReportsTableDataCell width="200px">{user?.name}</ReportsTableDataCell>
                        <ReportsTableDataCell>
                          <Text>
                            {user?.total?.total_mileage ? formatDistanceUnit(+user?.total?.total_mileage) : '- '}
                          </Text>
                        </ReportsTableDataCell>
                        <ReportsTableDataCell>
                          <Text>{user?.total?.regular_hours || '- '}</Text>
                        </ReportsTableDataCell>
                        <ReportsTableDataCell>
                          <Text>{user?.total?.regular_pay ? formatCurrency(+user?.total?.regular_pay) : '- '}</Text>
                        </ReportsTableDataCell>
                        {userStore?.companyInfo?.include_premium_pay ? (
                          <ReportsTableDataCell>
                            <Text>{user?.total?.premium_pay_hours || '- '}</Text>
                          </ReportsTableDataCell>
                        ) : null}
                        {userStore?.companyInfo?.include_premium_pay ? (
                          <ReportsTableDataCell>
                            <Text>{user?.total?.premium_pay_cost || '- '}</Text>
                          </ReportsTableDataCell>
                        ) : null}
                        <ReportsTableDataCell>
                          <Text>{user?.total?.daily_overtime_hours || '- '}</Text>
                        </ReportsTableDataCell>
                        <ReportsTableDataCell>
                          <Text>{user?.total?.double_overtime_hours || ''}</Text>
                        </ReportsTableDataCell>
                        <ReportsTableDataCell>
                          {' '}
                          <Text>{user?.total?.overtime_cost ? formatCurrency(user?.total?.overtime_cost) : '- '}</Text>
                        </ReportsTableDataCell>
                        <ReportsTableDataCell color="secondary.600">
                          <Text>{user?.total?.total_time || '- '}</Text>
                        </ReportsTableDataCell>
                        <ReportsTableDataCell color="secondary.700">
                          <Text>{user?.total?.total_cost ? formatCurrency(user?.total?.total_cost) : '- '}</Text>
                        </ReportsTableDataCell>
                        <ReportsTableDataCell color="secondary.700" width="180px">
                          <Text>
                            {user?.total?.total_mileage_cost ? formatCurrency(user?.total?.total_mileage_cost) : '- '}
                          </Text>
                        </ReportsTableDataCell>
                      </ReportsTableRow>
                    ))}

                  {data?.payload?.total ? (
                    <ReportsTableRow bg="primary.50" minWidth="100%" width="fit-content">
                      <ReportsTableDataCell fontWeight="semibold" width="200px">
                        {t('common:total')}:
                      </ReportsTableDataCell>
                      <ReportsTableDataCell fontWeight="semibold">
                        {data.payload.total?.total_mileage
                          ? formatDistanceUnit(+data.payload?.total?.total_mileage)
                          : '- '}
                      </ReportsTableDataCell>
                      <ReportsTableDataCell fontWeight="semibold">
                        {data.payload.total?.regular_hours || '- '}
                      </ReportsTableDataCell>
                      <ReportsTableDataCell fontWeight="semibold">
                        {data.payload.total?.regular_pay ? formatCurrency(+data.payload.total.regular_pay) : '0.00'}
                      </ReportsTableDataCell>
                      {userStore?.companyInfo?.include_premium_pay ? (
                        <ReportsTableDataCell>
                          <Text>{data.payload?.total?.premium_pay_hours || '- '}</Text>
                        </ReportsTableDataCell>
                      ) : null}
                      {userStore?.companyInfo?.include_premium_pay ? (
                        <ReportsTableDataCell>
                          <Text>{data.payload?.total?.premium_pay_cost || '- '}</Text>
                        </ReportsTableDataCell>
                      ) : null}
                      <ReportsTableDataCell fontWeight="semibold">
                        {data.payload.total?.overtime_hours || '- '}
                      </ReportsTableDataCell>
                      <ReportsTableDataCell fontWeight="semibold">
                        {data.payload.total?.double_overtime_hours || '- '}
                      </ReportsTableDataCell>
                      <ReportsTableDataCell fontWeight="semibold">
                        {data.payload.total?.overtime_cost ? formatCurrency(+data.payload.total.overtime_cost) : '0.00'}
                      </ReportsTableDataCell>
                      <ReportsTableDataCell fontWeight="semibold">
                        {data.payload.total?.total_time || '- '}
                      </ReportsTableDataCell>
                      <ReportsTableDataCell fontWeight="semibold">
                        {data.payload.total?.total_cost ? formatCurrency(+data.payload.total?.total_cost) : '0.00'}
                      </ReportsTableDataCell>
                      <ReportsTableDataCell fontWeight="semibold" width="180px">
                        {data.payload.total?.total_mileage_cost
                          ? formatCurrency(data.payload.total?.total_mileage_cost)
                          : '0.00'}
                      </ReportsTableDataCell>
                    </ReportsTableRow>
                  ) : null}
                </Tbody>
              </ScrollSyncPane>
            </Table>
          </div>
        </ScrollSync>
      </Box>
    </>
  );
};

export default ReportsByTotalTable;
